import { default as React, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import {
  selectFilteredVirtualCards,
  selectUsersFullPaginatedDataFromState,
} from 'src/app/VirtualCardsAllCards/redux/virtualCardsAllCards.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import BillPayBoxAndLinks from 'src/components/BillPayBoxAndLinks/BillPayBoxAndLinks'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useQueryParamArrayHandler } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { selectStatisticsDispatcherFromState } from 'src/redux/selectors/app.selectors'
import {
  selectBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFromState,
  selectBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

// TODO: add 50 when we add tabs, 25 when we add downloads
const HEADER_HEIGHT = 75

const BillPayAll: React.FC = () => {
  const translate = useTranslate()

  const billPayCardsActiveSourceUserOrViewerOrDelegateUserMeCount = useSelector(
    selectBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFromState,
  )
  const billPayCardsCardholderOrViewerMeActiveAndInactiveCount = useSelector(
    selectBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFromState,
  )
  const appName = useSelector(selectAppName)
  const queryParams = useSelector(selectQueryParams)
  const filteredVirtualCards = useSelector(selectFilteredVirtualCards)
  const usersFullPaginatedData = useSelector(
    selectUsersFullPaginatedDataFromState,
  )

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  const [clientHeight, setClientHeight] = useState<number | null>(
    window.innerHeight - HEADER_HEIGHT,
  )
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - HEADER_HEIGHT)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - HEADER_HEIGHT)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.BillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatistics,
      appStatisticsKeys.BillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatistics,
    ])
  }, [dispatchStatistics])

  return (
    <>
      <Helmet>
        <title>{`Bill Pay | ${appName}`}</title>
        <meta name="description" content={`Bill Pay page of ${appName}`} />
      </Helmet>
      <div
        css={css`
          height: 100%;
        `}
        ref={ref}
        id={'ref'}
      >
        <BackgroundPageContent noStackingIndex noPadding>
          <BillPayBoxAndLinks
            virtualCards={filteredVirtualCards}
            defaultTitle={
              <ObjectBoxTitle
                static={{
                  label: translate('Active'),
                  count:
                    billPayCardsActiveSourceUserOrViewerOrDelegateUserMeCount,
                }}
              />
            }
            emptyStateDesign={TableEmptyStateDesigns.AccountVirtualCardsActive}
            initialFilterValues={{
              sortBy: 'activeClosedUpdatedAt',
              sortDescending: false,
              keyword: queryParams.search || '',
              statuses: useQueryParamArrayHandler(queryParams.statuses),
              deactivatingIn: queryParams.deactivatingIn,
              cardholders: useQueryParamArrayHandler(queryParams.cardholders),
              creditCardId: useQueryParamArrayHandler(queryParams.creditCardId),
            }}
            totalCount={billPayCardsCardholderOrViewerMeActiveAndInactiveCount}
            usersFullPaginatedData={usersFullPaginatedData}
            clientHeight={clientHeight || 0}
          />
        </BackgroundPageContent>
      </div>
    </>
  )
}

export default BillPayAll
